<ai-form-dialog
  [visible]="visible() && !(access().onDenied === 'block' && !access().canOpen)"
  (visibleChange)="closeModal()"
  [title]="titleText()"
  [subtitle]="descriptionText()"
  [icon]="config().icon ?? d().icon"
  [width]="config().width ?? (isChat() ? d().chatWidth : d().width)"
  [busy]="starting()">
  @if (helpText()) {
    <i
      dialogHeaderMeta
      class="pi pi-question-circle ai-runner__help"
      [pTooltip]="helpText()"
      tooltipPosition="bottom"
      [attr.aria-label]="t().help"></i>
  }

  @if (denied()) {
    <ai-alert [severity]="readOnly() ? 'info' : 'warn'">{{ denied() }}</ai-alert>
  }
  @if (unavailable()) {
    <ai-alert severity="info">{{ unavailable() }}</ai-alert>
  }
  @if (missingPipeline()) {
    <ai-alert severity="warn">{{ missingPipeline() }}</ai-alert>
  }
  @if (error()) {
    @if (slot('error'); as tpl) {
      <ng-container *ngTemplateOutlet="tpl; context: { $implicit: slotContext() }" />
    } @else {
      <ai-alert severity="danger" [closable]="true" (closed)="error.set(null)">{{
        error()
      }}</ai-alert>
      @if (busyRunId()) {
        <button type="button" class="ai-btn ai-btn--sm" (click)="followBusyRun()">
          <i class="pi pi-eye" aria-hidden="true"></i> {{ t().followRun }}
        </button>
      }
    }
  }
  @if (slot('header'); as tpl) {
    <ng-container *ngTemplateOutlet="tpl; context: { $implicit: slotContext() }" />
  }

  <!-- Alvo: pipeline selecionável ou fixa -->
  @if (pipelineTarget(); as target) {
    @if (selectable() && (phase() === 'input' || isChat())) {
      <div class="ai-field ai-runner__target">
        <label for="ai-runner-pipeline" class="required">{{ t().pipeline }}</label>
        <p-select
          inputId="ai-runner-pipeline"
          [options]="pipelines()"
          optionLabel="name"
          optionValue="id"
          [ngModel]="pipelineId()"
          (ngModelChange)="pipelineId.set($event)"
          [disabled]="running() || messages().length > 0"
          [loading]="pipelinesLoading()"
          [filter]="true"
          appendTo="body"
          [placeholder]="t().selectPipeline" />
        @if (selectedPipeline()?.description) {
          <small class="ai-runner__muted">{{ selectedPipeline()?.description }}</small>
        }
      </div>
    } @else if (selectedPipeline() && !listMode()) {
      <div class="ai-runner__muted ai-runner__target-fixed">
        <i class="pi pi-sitemap" aria-hidden="true"></i> {{ selectedPipeline()?.name }}
      </div>
    }
  }

  @if (isChat()) {
    <!-- Modo chat -->
    <div class="ai-runner-chat">
      <div class="ai-runner-chat__list" aria-live="polite">
        @if (!messages().length && !running()) {
          <div class="ai-runner__muted ai-runner-chat__empty">
            {{ config().prompt?.help ?? 'Envie uma mensagem para começar.' }}
          </div>
        }
        @for (message of messages(); track $index) {
          <div
            class="ai-runner-chat__msg"
            [class.is-user]="message.role === 'user'"
            [class.is-error]="message.error">
            @if (message.role === 'assistant' && !message.error) {
              <markdown [data]="message.content" />
            } @else {
              <span class="ai-runner__pre">{{ message.content }}</span>
            }
          </div>
        }
        @if (running()) {
          <div class="ai-runner-chat__msg is-typing">
            <span class="ai-runner-chat__dots"><i></i><i></i><i></i></span>
            @if (live()) {
              @if (showSteps()) {
                <span class="ai-runner__muted">{{
                  steps().at(-1)?.step_name ?? 'Iniciando…'
                }}</span>
              } @else {
                <span class="ai-runner__muted">{{ percent() }}%</span>
              }
            }
          </div>
        }
      </div>
      <ng-container *ngTemplateOutlet="filesTpl" />
      <div class="ai-runner-chat__composer">
        <textarea
          class="p-textarea"
          rows="2"
          [placeholder]="
            config().prompt?.placeholder ??
            'Escreva sua mensagem (Enter envia, Shift+Enter quebra linha)'
          "
          [ngModel]="prompt()"
          (ngModelChange)="prompt.set($event)"
          (keydown)="onChatKeydown($event)"
          [disabled]="running()"></textarea>
        @if (running()) {
          <button type="button" class="ai-btn ai-btn--danger" (click)="cancel()">
            <i class="pi pi-stop" aria-hidden="true"></i>
          </button>
        } @else {
          <button
            type="button"
            class="ai-btn ai-btn--primary"
            [attr.aria-label]="t().send"
            [disabled]="!canSubmit()"
            (click)="submit()">
            <i
              class="pi"
              [class.pi-send]="!starting()"
              [class.pi-spin]="starting()"
              [class.pi-spinner]="starting()"></i>
          </button>
        }
      </div>
    </div>
  } @else {
    <!-- Entrada -->
    @if (phase() === 'input') {
      @if (readOnly()) {
        <!-- Sem permissão de executar: o modal é só consulta, com os resultados direto na lista. -->
        <div class="ai-field">
          <label>{{ config().historyLabel ?? t().historyReadOnly }}</label>
          @if (history().length) {
            <ul class="ai-runner-history__list ai-runner-history__list--inline">
              @for (item of history(); track item.id) {
                <li>
                  <ng-container
                    *ngTemplateOutlet="historyRow; context: { $implicit: item, close: false }" />
                </li>
              }
            </ul>
          } @else {
            <div class="ai-runner__muted">{{ t().historyEmpty }}</div>
          }
        </div>
      }
      @if (listMode() && pipelinesLoading()) {
        <div class="ai-runner__muted ai-runner__data-note">
          <i class="pi pi-spin pi-spinner" aria-hidden="true"></i> {{ t().loadingOptions }}
        </div>
      }
      @if (variants().length) {
        <div class="ai-field">
          <label class="required">{{ config().variantsLabel ?? 'Tipo de execução' }}</label>
          <div class="ai-runner-variants" role="radiogroup">
            @for (item of variants(); track item.key) {
              <div class="ai-runner-variants__item">
                <button
                  type="button"
                  role="radio"
                  class="ai-runner-variants__card"
                  [class.is-selected]="variantKey() === item.key"
                  [attr.aria-checked]="variantKey() === item.key"
                  [attr.aria-label]="item.label + (item.description ? ': ' + item.description : '')"
                  [disabled]="starting()"
                  (click)="selectVariant(item.key)"
                  (dblclick)="selectVariant(item.key); submit()">
                  <i [class]="item.icon ?? d().icon" aria-hidden="true"></i>
                  <span class="ai-runner-variants__label">{{ item.label }}</span>
                  @if (item.description) {
                    <span class="ai-runner-variants__desc">{{ item.description }}</span>
                  }
                </button>
                <!-- Ao passar o mouse: o mesmo cartão sobreposto, com o texto completo. -->
                @if (variantTruncated(item)) {
                  <div class="ai-runner-variants__tip" aria-hidden="true">
                    <i [class]="item.icon ?? d().icon"></i>
                    <span class="ai-runner-variants__label">{{ item.label }}</span>
                    <span class="ai-runner-variants__desc">{{ item.description }}</span>
                  </div>
                }
              </div>
            }
          </div>
        </div>
      }
      @if (!readOnly() && shownOptions().length) {
        <div class="ai-runner-options">
          <div class="ai-runner-options__title">{{ config().optionsLabel ?? 'Opções' }}</div>
          @for (option of shownOptions(); track option.key) {
            @switch (option.type) {
              @case ('checkbox') {
                <div class="ai-runner-options__check" [class.is-child]="!!option.parent">
                  <p-checkbox
                    [inputId]="'ai-opt-' + option.key"
                    [binary]="true"
                    [disabled]="starting()"
                    [ngModel]="!!optionValues()[option.key]"
                    (ngModelChange)="setOption(option.key, $event)" />
                  <label [for]="'ai-opt-' + option.key">
                    @if (option.icon) {
                      <i [class]="option.icon" aria-hidden="true"></i>
                    }
                    {{ option.label }}
                  </label>
                  @if (option.help) {
                    <i
                      class="pi pi-info-circle ai-runner__help"
                      [pTooltip]="option.help"
                      tooltipPosition="top"
                      aria-hidden="true"></i>
                  }
                </div>
              }
              @default {
                <div class="ai-field">
                  <label [for]="'ai-opt-' + option.key" [class.required]="option.required">
                    {{ option.label }}
                    @if (option.help) {
                      <i
                        class="pi pi-info-circle ai-runner__help"
                        [pTooltip]="option.help"
                        tooltipPosition="top"
                        aria-hidden="true"></i>
                    }
                  </label>
                  @switch (option.type) {
                    @case ('textarea') {
                      <textarea
                        [id]="'ai-opt-' + option.key"
                        class="p-textarea"
                        [rows]="option.rows ?? 3"
                        [placeholder]="option.placeholder ?? ''"
                        [disabled]="starting()"
                        [ngModel]="optionValues()[option.key]"
                        (ngModelChange)="setOption(option.key, $event)"></textarea>
                    }
                    @case ('text') {
                      <input
                        [id]="'ai-opt-' + option.key"
                        class="p-inputtext"
                        [placeholder]="option.placeholder ?? ''"
                        [disabled]="starting()"
                        [ngModel]="optionValues()[option.key]"
                        (ngModelChange)="setOption(option.key, $event)" />
                    }
                    @case ('number') {
                      <p-inputnumber
                        [inputId]="'ai-opt-' + option.key"
                        [min]="option.min"
                        [max]="option.max"
                        [disabled]="starting()"
                        [ngModel]="optionValues()[option.key]"
                        (ngModelChange)="setOption(option.key, $event)" />
                    }
                    @case ('select') {
                      @if (option.multiple) {
                        <p-multiselect
                          [inputId]="'ai-opt-' + option.key"
                          [options]="option.choices"
                          optionLabel="label"
                          optionValue="value"
                          [placeholder]="option.placeholder ?? t().select"
                          appendTo="body"
                          [disabled]="starting()"
                          [ngModel]="optionValues()[option.key]"
                          (ngModelChange)="setOption(option.key, $event)" />
                      } @else {
                        <p-select
                          [inputId]="'ai-opt-' + option.key"
                          [options]="option.choices"
                          optionLabel="label"
                          optionValue="value"
                          [showClear]="!option.required"
                          [placeholder]="option.placeholder ?? t().select"
                          appendTo="body"
                          [disabled]="starting()"
                          [ngModel]="optionValues()[option.key]"
                          (ngModelChange)="setOption(option.key, $event)" />
                      }
                    }
                    @case ('record') {
                      <p-autocomplete
                        [inputId]="'ai-opt-' + option.key"
                        [suggestions]="recordSuggestions()[option.key] ?? []"
                        optionLabel="label"
                        [delay]="300"
                        [forceSelection]="true"
                        [showClear]="true"
                        [dropdown]="true"
                        [placeholder]="option.placeholder ?? 'Buscar'"
                        appendTo="body"
                        [disabled]="starting()"
                        [ngModel]="recordChoices()[option.key] ?? null"
                        (ngModelChange)="pickRecord(option.key, $event)"
                        (completeMethod)="searchRecord(option.key, $event.query)" />
                    }
                  }
                </div>
              }
            }
          }
        </div>
      }
      @if (!readOnly() && hasPrompt()) {
        <div class="ai-field">
          <label for="ai-runner-prompt" [class.required]="config().prompt?.required">
            {{ config().prompt?.label ?? 'Prompt' }}
            @if (config().prompt?.help) {
              <i
                class="pi pi-info-circle"
                [pTooltip]="config().prompt?.help"
                aria-hidden="true"></i>
            }
          </label>
          <textarea
            id="ai-runner-prompt"
            class="p-textarea"
            rows="8"
            [placeholder]="config().prompt?.placeholder ?? 'Descreva o que a IA deve fazer'"
            [ngModel]="prompt()"
            (ngModelChange)="prompt.set($event)"></textarea>
        </div>
      }
      <ng-container *ngTemplateOutlet="filesTpl" />
      @if (config().data && mode() !== 'prompt') {
        <div class="ai-runner__muted ai-runner__data-note">
          <i class="pi pi-database" aria-hidden="true"></i>
          {{ config().dataLabel ?? 'Os dados desta tela serão enviados junto.' }}
        </div>
      }
      @if (slot('form'); as tpl) {
        <ng-container *ngTemplateOutlet="tpl; context: { $implicit: slotContext() }" />
      }
      @if (!readOnly() && mode() === 'preset' && !starting() && (!variants().length || variant())) {
        <div class="ai-runner__muted">{{ t().readyToRun }}</div>
      }
    } @else {
      <!-- Envio, andamento e resultado -->
      @if (live() && sent(); as s) {
        <ai-form-section
          sectionId="enviado"
          icon="pi pi-upload"
          [title]="t().sent"
          [collapsible]="true"
          [collapsed]="!sentOpen()"
          (collapsedChange)="sentOpen.set(!$event)">
          @if (s.input.prompt) {
            <div class="ai-runner__pre ai-runner__box">{{ s.input.prompt }}</div>
          }
          @if (s.fileNames.length) {
            <div class="ai-runner__muted">Arquivos: {{ s.fileNames.join(', ') }}</div>
          }
          @if (sentData(); as data) {
            <pre class="ai-runner__box">{{ data | json }}</pre>
          }
        </ai-form-section>
      }

      <div class="ai-runner__status">
        @if (run(); as r) {
          <ai-status-badge [status]="r.status" />
        }
        @if (variant(); as v) {
          <span class="ai-runner__muted"
            ><i [class]="v.icon ?? d().icon" aria-hidden="true"></i> {{ v.label }}</span
          >
        }
        @if (running()) {
          <span class="ai-runner__muted">{{ t().processing }}</span>
        }
      </div>

      @if (live() && showSteps() && steps().length) {
        @if (slot('steps'); as tpl) {
          <ng-container *ngTemplateOutlet="tpl; context: { $implicit: slotContext() }" />
        } @else {
          <ai-run-step-timeline [steps]="steps()" />
        }
      } @else if ((running() || (live() && !showSteps())) && slot('loading'); as tpl) {
        <ng-container *ngTemplateOutlet="tpl; context: { $implicit: slotContext() }" />
      } @else if ((running() || (live() && !showSteps())) && config().loading === 'spinner') {
        <div class="ai-runner__muted"><i class="pi pi-spin pi-spinner" aria-hidden="true"></i></div>
      } @else if ((running() || (live() && !showSteps())) && config().loading !== 'none') {
        <div
          class="ai-runner-progress"
          role="progressbar"
          [attr.aria-valuenow]="percent()"
          aria-valuemin="0"
          aria-valuemax="100">
          <div class="ai-runner-progress__bar" [style.width.%]="percent()"></div>
          <span class="ai-runner-progress__label">{{ percent() }}%</span>
        </div>
      }

      @if (phase() === 'done') {
        <ai-form-section sectionId="resultado" icon="pi pi-file" [title]="t().result">
          <div class="ai-runner__result">
            @if (customView()) {
              @if (resultTemplate(); as tpl) {
                <ng-container
                  *ngTemplateOutlet="tpl.template; context: { $implicit: resultContext()! }" />
              } @else if (fieldViews().length) {
                <div class="ai-runner-fields">
                  @for (view of fieldViews(); track view.field.path) {
                    <div
                      class="ai-runner-fields__item"
                      [attr.data-display]="view.field.display ?? 'text'">
                      @if (view.field.label) {
                        <div class="ai-runner-fields__label">
                          @if (view.field.icon) {
                            <i [class]="view.field.icon" aria-hidden="true"></i>
                          }
                          {{ view.field.label }}
                        </div>
                      }
                      @if (!view.found) {
                        <span class="ai-runner__muted">{{ t().notInformed }}</span>
                      } @else {
                        @switch (view.field.display ?? 'text') {
                          @case ('meter') {
                            <div class="ai-runner-meter" [attr.data-severity]="view.severity">
                              <span class="ai-runner-meter__value"
                                >{{ view.text }}<small>/{{ view.field.max ?? 10 }}</small></span
                              >
                              <div class="ai-runner-meter__track">
                                <div
                                  class="ai-runner-meter__bar"
                                  [style.width.%]="view.percent"></div>
                              </div>
                            </div>
                          }
                          @case ('badge') {
                            <span class="ai-runner-badge" [attr.data-severity]="view.severity">{{
                              view.text
                            }}</span>
                          }
                          @case ('list') {
                            @if (view.items.length) {
                              <ul class="ai-runner-fields__list">
                                @for (item of view.items; track $index) {
                                  <li>{{ item }}</li>
                                }
                              </ul>
                            } @else {
                              <div class="ai-runner__pre">{{ view.text }}</div>
                            }
                          }
                          @case ('markdown') {
                            <markdown [data]="view.text" />
                          }
                          @case ('json') {
                            <pre class="ai-runner__box">{{ view.value | json }}</pre>
                          }
                          @default {
                            <div class="ai-runner__pre">{{ view.text }}</div>
                          }
                        }
                      }
                    </div>
                  }
                </div>
              } @else {
                <markdown [data]="viewMarkdown() ?? ''" />
              }
            } @else {
              @switch (resultFormat()) {
                @case ('json') {
                  <pre class="ai-runner__box">{{ resultJson() ?? output() | json }}</pre>
                }
                @case ('text') {
                  <div class="ai-runner__pre ai-runner__box">{{ output() }}</div>
                }
                @case ('html') {
                  <div class="ai-runner__box" [innerHTML]="output()"></div>
                }
                @default {
                  <markdown [data]="output()" />
                }
              }
            }
            <div class="ai-runner__result-tools">
              <ai-result-export
                [content]="output()"
                [json]="resultJson()"
                [fileName]="exportName()"
                [title]="variant()?.label ?? titleText()" />
              @if (canToggleRaw()) {
                <button
                  type="button"
                  class="ai-btn ai-btn--sm ai-btn--ghost"
                  (click)="rawView.set(!rawView())">
                  <i [class]="rawView() ? 'pi pi-eye' : 'pi pi-code'" aria-hidden="true"></i>
                  {{ rawView() ? t().viewFormatted : t().viewRaw }}
                </button>
              }
            </div>
          </div>
          @if (canRate() && !rejecting()) {
            <ai-run-feedback [runId]="run()!.id" />
          }
        </ai-form-section>
      }
    }

    <!-- Recusa do resultado: motivo obrigatório, nada é gravado e volta para a revisão -->
    @if (canRate() && rejecting()) {
      <ai-form-section sectionId="recusa" icon="pi pi-thumbs-down" title="Recusar o resultado">
        <ai-run-feedback
          mode="reject"
          [runId]="run()!.id"
          [rejectLabel]="config().rejectFeedbackOption ? 'Recusar e revisar' : 'Recusar'"
          (saved)="onRejected($event)"
          (cancelled)="rejecting.set(false)" />
      </ai-form-section>
    }

    <!-- Atualizações da tela pelo JSON do resultado -->
    @if (bindingPreview().length) {
      <ai-form-section sectionId="aplicar" icon="pi pi-sync" [title]="t().applySection">
        <ul class="ai-runner-bindings">
          @for (item of bindingPreview(); track item.binding.key) {
            <li [class.is-missing]="!item.found">
              <span class="ai-runner-bindings__label">{{ item.binding.label }}</span>
              <span class="ai-runner-bindings__value">{{ item.display }}</span>
            </li>
          }
        </ul>
        @if (applied()) {
          <ai-alert severity="info">{{ t().applied }}</ai-alert>
        }
      </ai-form-section>
    }

    <!-- Ações sobre o resultado, declaradas por quem usa o modal -->
    @if (actionStates().length) {
      <ai-form-section
        sectionId="acoes"
        icon="pi pi-bolt"
        [title]="config().actionsLabel ?? 'O que fazer com o resultado'">
        <ul class="ai-runner-actions">
          @for (state of actionStates(); track state.action.key) {
            <li [attr.data-status]="state.status">
              <label class="ai-runner-actions__row">
                <input
                  type="checkbox"
                  [checked]="state.selected"
                  [disabled]="state.locked || state.status === 'done' || actionsRunning()"
                  (change)="toggleAction(state.action.key)" />
                <i [class]="state.action.icon ?? 'pi pi-check-square'" aria-hidden="true"></i>
                <span class="ai-runner-actions__label">{{ state.action.label }}</span>
                <span class="ai-runner-actions__status">
                  @switch (state.status) {
                    @case ('running') {
                      <i class="pi pi-spin pi-spinner" [attr.aria-label]="t().saving"></i>
                    }
                    @case ('done') {
                      <i class="pi pi-check-circle" [attr.aria-label]="t().done"></i>
                    }
                    @case ('error') {
                      <i class="pi pi-exclamation-triangle" [attr.aria-label]="t().error"></i>
                    }
                  }
                </span>
              </label>
              @if (state.action.description) {
                <small class="ai-runner__muted">{{ state.action.description }}</small>
              }
              @if (editingAction() === state.action.key) {
                <textarea
                  class="ai-runner__pre ai-runner-actions__editor"
                  rows="10"
                  [attr.aria-label]="t().editAction + ': ' + state.action.label"
                  [disabled]="state.status === 'done' || actionsRunning()"
                  [ngModel]="actionEdits()[state.action.key]"
                  (ngModelChange)="setActionEdit(state.action.key, $event)"></textarea>
                <markdown class="ai-runner-actions__md" [data]="actionEdits()[state.action.key]" />
              } @else if (state.preview) {
                <div class="ai-runner__pre ai-runner-actions__preview">{{ state.preview }}</div>
              }
              @if (state.action.content && state.status !== 'done') {
                <button
                  type="button"
                  class="ai-btn ai-btn--sm"
                  [disabled]="actionsRunning()"
                  (click)="toggleEdit(state)">
                  <i class="pi pi-pencil" aria-hidden="true"></i>
                  {{ editingAction() === state.action.key ? t().closeEdit : t().editContent }}
                </button>
              }
              @if (state.error) {
                <small class="ai-runner__error">{{ state.error }}</small>
              }
            </li>
          }
        </ul>
      </ai-form-section>
    }
  }

  <!-- Linha de uma execução anterior (lista do rodapé e lista de só consulta). -->
  <ng-template #historyRow let-item let-closePanel="close">
    <button
      type="button"
      class="ai-runner-history__item"
      (click)="openHistoryRun(item); closePanel && historyOpen.set(false)">
      <span class="ai-runner-history__name">{{ item.pipeline?.name ?? t().unknownRun }}</span>
      <small class="ai-runner__muted">{{ historyStatus(item) }}</small>
      <small class="ai-runner__muted">
        {{ historyWhen(item) }}
        @if (item.requester?.name) {
          · {{ item.requester?.name }}
        }
      </small>
    </button>
  </ng-template>

  <ng-template #filesTpl>
    @if (filesEnabled()) {
      <div class="ai-field ai-runner-files">
        <div class="ai-runner-files__row">
          <label class="ai-btn ai-btn--sm" [class.is-disabled]="running()">
            <i class="pi pi-paperclip" aria-hidden="true"></i> {{ t().attachFiles }}
            <input
              type="file"
              hidden
              multiple
              [accept]="config().files?.accept ?? ''"
              [disabled]="running()"
              (change)="onFilesSelected($event)" />
          </label>
          <small class="ai-runner__muted">{{ t().maxFileSize(maxSizeMb()) }}</small>
        </div>
        @if (files().length) {
          <ul class="ai-runner-files__list">
            @for (file of files(); track $index) {
              <li>
                <i class="pi pi-file" aria-hidden="true"></i>
                <span>{{ file.name }}</span>
                <small class="ai-runner__muted">{{ formatSize(file.size) }}</small>
                <button
                  type="button"
                  class="ai-alert__close"
                  [attr.aria-label]="t().remove"
                  (click)="removeFile($index)">
                  <i class="pi pi-times" aria-hidden="true"></i>
                </button>
              </li>
            }
          </ul>
        }
        @if (filesError()) {
          <small class="ai-runner__error">{{ filesError() }}</small>
        }
      </div>
    }
  </ng-template>

  <div dialogFooter class="ai-actions">
    @if (slot('footer'); as tpl) {
      <ng-container *ngTemplateOutlet="tpl; context: { $implicit: slotContext() }" />
    }
    @if (history().length && !readOnly()) {
      <div class="ai-runner-history">
        <button
          type="button"
          class="ai-btn ai-btn--ghost"
          [attr.aria-expanded]="historyOpen()"
          (click)="historyOpen.set(!historyOpen())">
          <i class="pi pi-history" aria-hidden="true"></i>
          {{ t().history }} ({{ history().length }})
        </button>
        @if (historyOpen()) {
          <div class="ai-runner-history__panel">
            <div class="ai-runner-history__head">
              <span>{{ t().history }}</span>
              <button
                type="button"
                class="ai-btn ai-btn--ghost ai-btn--sm"
                [attr.aria-label]="t().closeList"
                (click)="historyOpen.set(false)">
                <i class="pi pi-times" aria-hidden="true"></i>
              </button>
            </div>
            <ul class="ai-runner-history__list" role="menu">
              @for (item of history(); track item.id) {
                <li role="none">
                  <ng-container
                    *ngTemplateOutlet="historyRow; context: { $implicit: item, close: true }" />
                </li>
              }
            </ul>
          </div>
        }
      </div>
    }
    <span class="ai-actions__spacer"></span>
    <button type="button" class="ai-btn ai-btn--ghost" [disabled]="starting()" (click)="close()">
      {{ t().close }}
    </button>
    @if (!isChat()) {
      @if (running()) {
        <button type="button" class="ai-btn ai-btn--danger" (click)="cancel()">
          <i class="pi pi-stop" aria-hidden="true"></i> {{ t().cancelRun }}
        </button>
      } @else if (phase() === 'input') {
        @if (!readOnly()) {
          <button
            type="button"
            class="ai-btn ai-btn--primary"
            [disabled]="!canSubmit()"
            (click)="submit()">
            <i
              class="pi"
              [class.pi-play]="!starting()"
              [class.pi-spinner]="starting()"
              [class.pi-spin]="starting()"
              aria-hidden="true"></i>
            {{ config().submitLabel ?? t().run }}
          </button>
        }
      } @else {
        @if (bindingPreview().length && !applied() && config().applyResult !== 'auto') {
          <button
            type="button"
            class="ai-btn ai-btn--primary"
            [disabled]="applying()"
            (click)="applyBindings()">
            <i class="pi pi-check" aria-hidden="true"></i> {{ t().apply }}
          </button>
        }
        @if (pendingActions().length && !rejecting()) {
          <button
            type="button"
            class="ai-btn ai-btn--primary"
            [disabled]="actionsRunning()"
            (click)="applyActions()">
            <i
              class="pi"
              [class.pi-check]="!actionsRunning()"
              [class.pi-spinner]="actionsRunning()"
              [class.pi-spin]="actionsRunning()"
              aria-hidden="true"></i>
            {{ config().applyActionsLabel ?? 'Aplicar' }}
          </button>
        }
        @if (canRate() && !rejecting()) {
          <button
            type="button"
            class="ai-btn ai-btn--danger"
            [disabled]="actionsRunning()"
            title="Não usar nada deste resultado e voltar para a revisão com o motivo"
            (click)="rejecting.set(true)">
            <i class="pi pi-thumbs-down" aria-hidden="true"></i> Recusar
          </button>
        }
        @if (canRunAgain()) {
          <button type="button" class="ai-btn" [disabled]="actionsRunning()" (click)="again()">
            <i class="pi pi-replay" aria-hidden="true"></i> {{ t().again }}
          </button>
        }
      }
    } @else if (bindingPreview().length && !applied() && config().applyResult !== 'auto') {
      <button
        type="button"
        class="ai-btn ai-btn--primary"
        [disabled]="applying()"
        (click)="applyBindings()">
        <i class="pi pi-check" aria-hidden="true"></i> {{ t().apply }}
      </button>
    }
  </div>
</ai-form-dialog>
